/**
 * Conference recording GraphQL client.
 *
 * Bypasses the @scrile/api-provider package so we don't need to rebuild the
 * data-providers npm package every time we add a recording-related field.
 * Uses the same auth token the rest of the app uses for uploads.
 */
import config from "../config";
import { getAuthToken } from "./providers";

export interface RecordingTranscriptionSegment {
  userId: string | null;
  screenName: string | null;
  start: number;
  end: number;
  text: string;
}

export interface RecordingTranscription {
  status: "pending" | "running" | "done" | "failed";
  error: string | null;
  updatedAt: string | null;
  segments: RecordingTranscriptionSegment[];
}

export interface RecordingParticipant {
  userId: string;
  screenName: string | null;
}

export interface RecordingAudioTrack {
  userId: string;
  screenName: string | null;
  filename: string;
  mimeType: string | null;
  size: number;
}

export interface RecordingMeta {
  id: string;
  filename: string;
  url: string;
  /** Poster image (single-frame JPEG) extracted at upload time. */
  thumbnailUrl: string | null;
  format: string | null;
  fileSize: number;
  durationSeconds: number | null;
  storage: string | null;
  createdTime: string | null;
  recordedBy: string | null;
  conferenceId: string | null;
  threadId: string | null;
  participants: RecordingParticipant[];
  audioTracks: RecordingAudioTrack[];
  language: string | null;
  transcription: RecordingTranscription | null;
}

const RECORDING_FIELDS = `
  id
  filename
  url
  thumbnailUrl
  format
  fileSize
  durationSeconds
  storage
  createdTime
  recordedBy
  conferenceId
  threadId
  language
  participants { userId screenName }
  audioTracks { userId screenName filename mimeType size }
  transcription {
    status
    error
    updatedAt
    segments { userId screenName start end text }
  }
`;

function getGraphqlUrl(): string {
  const apiUrl = config.apiUrl || "/graphql";
  if (apiUrl.startsWith("/")) return window.location.origin + apiUrl;
  return apiUrl;
}

async function graphqlRequest<T>(query: string, variables: Record<string, unknown>): Promise<T> {
  const token = await getAuthToken();
  const resp = await fetch(getGraphqlUrl(), {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: token } : {}),
    },
    body: JSON.stringify({ query, variables }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`GraphQL HTTP ${resp.status}: ${text.slice(0, 200)}`);
  }
  const body = await resp.json();
  if (body.errors && body.errors.length > 0) {
    throw new Error(body.errors[0].message || "GraphQL error");
  }
  return body.data as T;
}

export async function fetchRecording(id: string): Promise<RecordingMeta | null> {
  const data = await graphqlRequest<{ recording: RecordingMeta | null }>(
    `query($id: ID!) { recording(id: $id) { ${RECORDING_FIELDS} } }`,
    { id },
  );
  return data.recording;
}

export async function fetchRecordings(threadId?: string): Promise<RecordingMeta[]> {
  const data = await graphqlRequest<{ recordings: RecordingMeta[] }>(
    `query($threadId: ID) { recordings(threadId: $threadId) { ${RECORDING_FIELDS} } }`,
    { threadId: threadId || null },
  );
  return data.recordings || [];
}

export async function retranscribeRecording(id: string): Promise<RecordingMeta> {
  const data = await graphqlRequest<{ retranscribeRecording: RecordingMeta }>(
    `mutation($id: ID!) { retranscribeRecording(id: $id) { ${RECORDING_FIELDS} } }`,
    { id },
  );
  return data.retranscribeRecording;
}
